import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import socketClusterClient from 'socketcluster-client';
import { useAuth } from './AuthContext';

const SocketClusterContext = createContext();

export const useSocketCluster = () => useContext(SocketClusterContext);

// Backward compatibility with old SocketContext
export const useSocket = () => useContext(SocketClusterContext);

export const SocketClusterProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const { user } = useAuth();
    const [error, setError] = useState(null);
    const channelRef = useRef(null);

    useEffect(() => {
        console.log('SocketCluster useEffect - User:', user);
        console.log('SocketCluster useEffect - Has token?', user?.token);

        // Changed condition to just check for user ID since that's what we need for connection
        if (user && user.id) {
            console.log('Creating SocketCluster connection for user:', user.id);
            try {
                // Create SocketCluster client connection
                const socketUrl = new URL(process.env.REACT_APP_SOCKET_URL);
                const newSocket = socketClusterClient.create({
                    hostname: socketUrl.hostname,
                    port: socketUrl.port || (socketUrl.protocol === 'https:' ? 443 : 80),
                    secure: socketUrl.protocol === 'https:',
                    rejectUnauthorized: false, // Allow self-signed certificates in dev
                    path: '/socketcluster/',  // Important: must match server config.json
                    query: {
                        userId: user.id
                    },
                    autoConnect: true,
                    autoReconnect: true,
                    autoReconnectOptions: {
                        initialDelay: 2000,
                        randomness: 1000,
                        multiplier: 1.5,
                        maxDelay: 10000
                    }
                });

                // Add Socket.IO compatibility methods
                newSocket.on = (eventName, callback) => {
                    console.log(`Socket.IO compatibility: on('${eventName}')`);
                    // For SocketCluster, we'll use channel subscription for notifications
                    if (eventName === 'notifications' || eventName === 'refetchData') {
                        const channel = newSocket.subscribe(`user:${user.id}`);
                        (async () => {
                            for await (let data of channel) {
                                callback(data);
                            }
                        })();
                    }
                };

                newSocket.off = (eventName, callback) => {
                    console.log(`Socket.IO compatibility: off('${eventName}')`);
                    // For now, just log - proper cleanup happens in useEffect return
                };

                newSocket.emit = (eventName, data) => {
                    console.log(`Socket.IO compatibility: emit('${eventName}')`, data);
                    // Map Socket.IO emit to SocketCluster operations
                    if (eventName === 'sendMessage') {
                        const recipientId = data.recipientId || data.userId;
                        if (recipientId) {
                            newSocket.transmitPublish(`user:${recipientId}`, data);
                        }
                    } else if (eventName === 'notificationAck') {
                        // Handle notification acknowledgment via RPC
                        console.log('Sending notificationAck via RPC:', data.notificationId);
                        newSocket.invoke('notificationAck', data).then(() => {
                            console.log('✓ Notification acknowledged:', data.notificationId);
                        }).catch(err => {
                            console.error('✗ Failed to acknowledge notification:', err);
                        });
                    }
                };

                // Log initial socket state
                console.log('Socket created with initial state:', newSocket.state);

                // Subscribe to user's personal channel IMMEDIATELY
                // The channel will buffer messages until connection is established
                const userChannel = `user:${user.id}`;
                channelRef.current = newSocket.subscribe(userChannel);
                console.log(`Subscribed to channel: ${userChannel} (will connect when socket opens)`);

                // Connection event handlers
                (async () => {
                    for await (let event of newSocket.listener('connect')) {
                        console.log('✓ Connected to SocketCluster server', event);
                        console.log(`✓ Channel ${userChannel} is now active and receiving messages`);
                        setIsConnected(true);
                        setError(null);
                    }
                })();

                (async () => {
                    for await (let event of newSocket.listener('disconnect')) {
                        console.log('✗ Disconnected from server:', event);
                        setIsConnected(false);
                    }
                })();

                (async () => {
                    for await (let { error: connectionError } of newSocket.listener('error')) {
                        console.error('✗ Socket error:', connectionError);
                        setError('Connection error, please try again.');
                    }
                })();

                // Check socket state periodically to update connection status
                const stateCheckInterval = setInterval(() => {
                    const currentState = newSocket.state;
                    console.log('Socket state check:', currentState);

                    if (currentState === 'open') {
                        setIsConnected(true);
                        setError(null);
                    } else if (currentState === 'closed') {
                        setIsConnected(false);
                    }
                }, 1000);

                setSocket(newSocket);
                console.log('SocketCluster socket created successfully');

                return () => {
                    console.log('Cleaning up SocketCluster connection');
                    clearInterval(stateCheckInterval);
                    if (channelRef.current) {
                        channelRef.current.unsubscribe();
                    }
                    newSocket.disconnect();
                };
            } catch (err) {
                console.error('Error while initializing SocketCluster:', err);
                console.error('Error details:', err.message, err.stack);
                setError(`Failed to connect to socket: ${err.message}`);
            }
        } else {
            setSocket(null);
            setIsConnected(false);
        }
    }, [user]);

    // Send message to another user via their channel
    const sendMessage = useCallback(async (recipientId, message) => {
        if (socket && isConnected) {
            try {
                // Publish message to recipient's channel
                await socket.transmitPublish(`user:${recipientId}`, {
                    from: user.id,
                    ...message,
                    timestamp: new Date().toISOString()
                });
                console.log(`Message sent to user:${recipientId}`);
            } catch (err) {
                console.error('Error while sending message:', err);
                setError('Failed to send message.');
            }
        } else {
            setError('No active socket connection.');
        }
    }, [socket, isConnected, user]);

    // Listen for incoming messages on user's channel
    const onRefetchData = useCallback((callback) => {
        if (!channelRef.current) {
            console.warn('onRefetchData called but channel not ready yet. Waiting...');

            // Wait for channel to be ready
            const checkInterval = setInterval(() => {
                if (channelRef.current) {
                    clearInterval(checkInterval);
                    console.log('Channel is now ready, setting up listener');
                    setupChannelListener(callback);
                }
            }, 100);

            // Return cleanup function
            return () => {
                clearInterval(checkInterval);
            };
        }

        return setupChannelListener(callback);

        function setupChannelListener(callback) {
            try {
                console.log('Setting up channel listener for notifications');

                // Listen for messages on the user's channel
                (async () => {
                    for await (let data of channelRef.current) {
                        console.log('📩 Received notification via channel:', data);
                        callback(data, socket);
                    }
                })();

                // Return cleanup function
                return () => {
                    console.log('Cleaned up channel listener');
                };
            } catch (err) {
                console.error('Error while handling channel data:', err);
                setError('Failed to set up data listener.');
            }
        }
    }, [socket]);

    // Invoke RPC methods on the server
    const invokeRPC = useCallback(async (method, data) => {
        if (socket && isConnected) {
            try {
                const response = await socket.invoke(method, data);
                return response;
            } catch (err) {
                console.error(`RPC ${method} failed:`, err);
                throw err;
            }
        } else {
            throw new Error('No active socket connection');
        }
    }, [socket, isConnected]);

    const value = {
        socket,
        isConnected,
        sendMessage,
        onRefetchData,
        invokeRPC,
        error,
    };

    return (
        <SocketClusterContext.Provider value={value}>
            {children}
            {error && <div className="socket-error">{error}</div>}
        </SocketClusterContext.Provider>
    );
};

// Export SocketProvider as alias for backward compatibility
export const SocketProvider = SocketClusterProvider;